<script setup lang="ts">
import { defineAsyncComponent, getCurrentInstance } from 'vue';
import { RouterView } from 'vue-router';
import { useAppOptionStore } from '@/stores/app-option';
import router from './router';
import { ProgressFinisher } from '@marcoschulte/vue3-progress';

// Lazy load components
const AppHeader = defineAsyncComponent(() =>
    import('@/components/app/Header.vue')
);
const AppSidebar = defineAsyncComponent(() =>
    import('@/components/app/Sidebar.vue')
);
const AppSidebarRight = defineAsyncComponent(() =>
    import('@/components/app/SidebarRight.vue')
);
const AppTopMenu = defineAsyncComponent(() =>
    import('@/components/app/TopMenu.vue')
);
const AppFooter = defineAsyncComponent(() =>
    import('@/components/app/Footer.vue')
);
const AppThemePanel = defineAsyncComponent(() =>
    import('@/components/app/ThemePanel.vue')
);

const appOption = useAppOptionStore();
const internalInstance = getCurrentInstance();
const progresses = [] as ProgressFinisher[];

// Progress bar import
const useProgressBar = async () => {
  const { useProgress } = await import('@marcoschulte/vue3-progress');
  return useProgress();
};

router.beforeEach(async (to, from) => {
  const progress = await useProgressBar();
  progresses.push(progress.start());
  appOption.appSidebarMobileToggled = false;
  document.body.scrollTop = 0;
  document.documentElement.scrollTop = 0;
  return true;
});

router.afterEach((to, from) => {
  progresses.pop()?.finish();
});
</script>

<template>
  <div class="app" v-bind:class="{
  'app-header-menu-search-toggled': appOption.appHeaderSearchToggled,
  'app-header-fixed': appOption.appHeaderFixed,
  'app-sidebar-fixed': appOption.appSidebarFixed,
  'app-sidebar-grid': appOption.appSidebarGrid,
  'app-sidebar-toggled': appOption.appSidebarToggled,
  'app-sidebar-collapsed': appOption.appSidebarCollapsed,
  'app-sidebar-mobile-toggled': appOption.appSidebarMobileToggled,
  'app-sidebar-mobile-closed': appOption.appSidebarMobileClosed,
  'app-sidebar-end-toggled': appOption.appSidebarEndToggled,
  'app-sidebar-end-mobile-toggled': appOption.appSidebarEndMobileToggled,
  'app-content-full-height': appOption.appContentFullHeight,
  'app-content-full-width': appOption.appSidebarHide,
  'app-without-sidebar': appOption.appSidebarHide,
  'app-with-end-sidebar': appOption.appSidebarEnd,
  'app-with-wide-sidebar': appOption.appSidebarWide,
  'app-with-hover-sidebar': appOption.appSidebarHover,
  'app-with-top-menu': appOption.appTopMenu,
  'app-with-two-sidebar': appOption.appSidebarTwo,
  'pt-0': appOption.appHeaderHide,
  'app-boxed-layout': appOption.appBoxedLayout,
  'app-footer-fixed': appOption.appFooterFixed,
  'app-sidebar-minified': appOption.appSidebarMinified,
  'app-gradient-enabled': appOption.appGradientEnabled
 }">
    <vue3-progress-bar />
    <Suspense>
      <template #default>
        <app-header v-show="!appOption.appHeaderHide" />
      </template>
      <template #fallback>
        <div>Loading header...</div>
      </template>
    </Suspense>

    <Suspense>
      <template #default>
        <app-sidebar v-show="!appOption.appSidebarHide" />
      </template>
      <template #fallback>
        <div>Loading sidebar...</div>
      </template>
    </Suspense>

    <Suspense>
      <template #default>
        <app-sidebar-right v-show="appOption.appSidebarTwo" />
      </template>
      <template #fallback>
        <div>Loading right sidebar...</div>
      </template>
    </Suspense>
<!--
    <Suspense>
      <template #default>
        <app-top-menu v-show="appOption.appTopMenu" />
      </template>
      <template #fallback>
        <div>Loading top menu...</div>
      </template>
    </Suspense>
    -->

    <div class="app-content p-3" v-bind:class="appOption.appContentClass">
      <router-view></router-view>
    </div>

    <Suspense>
      <template #default>
        <app-footer v-show="appOption.appFooter" />
      </template>
      <template #fallback>
        <div>Loading footer...</div>
      </template>
    </Suspense>

    <Suspense>
      <template #default>
        <app-theme-panel />
      </template>
      <template #fallback>
        <div>Loading theme panel...</div>
      </template>
    </Suspense>
  </div>
</template>